import React, {PropsWithChildren, useEffect, useRef} from "react"
import {AsyncResultIdleRequestState} from "../../types/asyncResultIdle"
import classNames from "classnames"
import Icon from "../Icon/Icon"
import {useClickAway} from "react-use"
import {useEsc} from "../../hooks/useEsc"

interface AsyncModalProps<T, E> {
	classname?: string
	title?: string
	secondaryTitle?: string
	buttonText: string
	buttonClassname: string
	cancelButtonText?: string
	cancelButtonClassname?: string
	secondaryButtonText?: string
	secondaryButtonClassname?: string
	onSecondaryButtonClick?: Function
	state: AsyncResultIdleRequestState<T, E>
	onSubmit: Function
	onSuccess?: Function
	onSuccessDelay?: number
	successText: string
	successClassname?: string
	errorToText: (err: E) => string
	successComponent?: (value: T) => JSX.Element
	close: () => void
	disableCloseOnSuccess?: boolean
	enableClickAway?: boolean
	okComponent?: JSX.Element
	blockBodyScroll?: boolean
	outerChildren?: JSX.Element
	readOnly?: boolean
	buttonDisabled?: boolean
	cancelButtonDisabled?: boolean
	formRef?: React.RefObject<HTMLFormElement>
}

function Idle<T, E>(props: PropsWithChildren<AsyncModalProps<T, E>>) {
	const buttonClassname = classNames("button", props.buttonClassname)
	const secondaryButtonClassname = classNames("button", props.secondaryButtonClassname)
	const cancelButtonClassname = classNames("button", props.cancelButtonClassname)

	const handleSecondaryClick = () => {
		if (props.formRef?.current?.reportValidity()) {
			props.onSecondaryButtonClick?.()
		}
	}

	return (
		<>
			<section className="modal-card-body">
				<fieldset disabled={props.readOnly}>{props.children}</fieldset>
			</section>
			<footer className="modal-card-foot">
				{!props.readOnly && (
					<>
						{props.cancelButtonText && (
							<input
								type="button"
								className={cancelButtonClassname}
								value={props.cancelButtonText}
								disabled={props.cancelButtonDisabled}
								onClick={props.close}
							/>
						)}
						{props.secondaryButtonText && (
							<input
								type="submit"
								className={secondaryButtonClassname}
								value={props.secondaryButtonText}
								disabled={props.buttonDisabled}
								onClick={(e) => {
									e.preventDefault()
									handleSecondaryClick()
								}}
							/>
						)}
						<input type="submit" className={buttonClassname} value={props.buttonText} disabled={props.buttonDisabled} />
					</>
				)}
			</footer>
		</>
	)
}

function Pending<T, E>(props: PropsWithChildren<AsyncModalProps<T, E>>) {
	const buttonClassname = classNames("button", "is-loading", props.buttonClassname)
	const secondaryButtonClassname = classNames("button", props.secondaryButtonClassname)
	const cancelButtonClassname = classNames("button", props.cancelButtonClassname)
	return (
		<>
			<section className="modal-card-body">
				<fieldset disabled>{props.children}</fieldset>
			</section>
			<footer className="modal-card-foot">
				{!props.readOnly && (
					<>
						{props.cancelButtonText && (
							<button disabled className={cancelButtonClassname}>
								{props.cancelButtonText}
							</button>
						)}
						{props.secondaryButtonText && (
							<button disabled className={secondaryButtonClassname}>
								{props.secondaryButtonText}
							</button>
						)}
						<button disabled className={buttonClassname}>
							{props.buttonText}
						</button>
					</>
				)}
			</footer>
		</>
	)
}

function Ok<T, E>(props: PropsWithChildren<AsyncModalProps<T, E> & {value: T}>) {
	useEffect(
		props.onSuccessDelay
			? () => {
					setTimeout(() => props.onSuccess?.(), props.onSuccessDelay)
			  }
			: () => props.onSuccess?.(),
		[]
	)

	useEffect(() => {
		if (!props.disableCloseOnSuccess) {
			setTimeout(() => props.close(), 1500)
		}
	}, [])

	if (props.successComponent) return props.successComponent(props.value)

	return props.okComponent ? (
		<>
			<section className="modal-card-body">{props.okComponent}</section>
		</>
	) : (
		<>
			<section className="modal-card-body">
				<fieldset disabled>{props.children}</fieldset>
			</section>
			<footer className="modal-card-foot">
				{!props.readOnly && <div className={props.successClassname ?? "has-text-success"}>{props.successText}</div>}
			</footer>
		</>
	)
}

function Err<T, E>(props: PropsWithChildren<AsyncModalProps<T, E>> & {err: E}) {
	const buttonClassname = classNames("button", props.buttonClassname)
	const secondaryButtonClassname = classNames("button", props.secondaryButtonClassname)
	const cancelButtonClassname = classNames("button", props.cancelButtonClassname)

	const handleSecondaryClick = () => {
		if (props.formRef?.current?.reportValidity()) {
			props.onSecondaryButtonClick?.()
		}
	}

	return (
		<>
			<section className="modal-card-body">
				<fieldset disabled={props.readOnly}>{props.children}</fieldset>
			</section>
			<footer className="modal-card-foot">
				<div className="has-text-danger is-align-self-center">{props.errorToText(props.err)}</div>
				{!props.readOnly && (
					<>
						{props.cancelButtonText && (
							<input
								type="button"
								className={cancelButtonClassname}
								value={props.cancelButtonText}
								disabled={props.cancelButtonDisabled}
								onClick={props.close}
							/>
						)}
						{props.secondaryButtonText && (
							<input
								type="submit"
								className={secondaryButtonClassname}
								value={props.secondaryButtonText}
								onClick={(e) => {
									e.preventDefault()
									handleSecondaryClick()
								}}
							/>
						)}
						<input type="submit" className={buttonClassname} value={props.buttonText} />
					</>
				)}
			</footer>
		</>
	)
}

export function AsyncResultModal<T, E>({
	buttonDisabled = false,
	enableClickAway = true,
	blockBodyScroll = false,
	...props
}: PropsWithChildren<AsyncModalProps<T, E>>) {
	// @TODO Remove delay on call. Search for: Dela
	const defaultProps = {buttonDisabled, enableClickAway, blockBodyScroll}
	const formRef = useRef<HTMLFormElement>(null)
	const content = props.state.match(
		() => <Idle {...defaultProps} {...props} formRef={formRef} />,
		() => <Pending {...defaultProps} {...props} />,
		(v) => <Ok value={v} {...defaultProps} {...props} />,
		(err) => <Err {...props} {...defaultProps} err={err} formRef={formRef} />
	)

	const ref = useRef(null)

	useEsc(props.close)

	if (enableClickAway) {
		useClickAway(ref, () => props.close())
	}

	useEffect(() => {
		if (blockBodyScroll) {
			const body = document.querySelector("body") as HTMLBodyElement
			body.classList.add("no-body-scroll")
			return () => {
				body.classList.remove("no-body-scroll")
			}
		}
	})

	return (
		<div className={classNames("modal", "is-active", props.classname)}>
			{props.outerChildren}
			<div className="modal-background" />
			<form
				ref={formRef}
				{...(props.readOnly
					? {}
					: {
							onSubmit: (e) => {
								e.preventDefault()
								props.onSubmit()
							},
					  })}
			>
				<div className="modal-card" ref={ref}>
					<header className="modal-card-head">
						<label className="delete" onClick={() => props.close()}>
							{" "}
							<Icon icon="interface-delete-interface-essential" size={16} />
						</label>
					</header>
					{props.title && (
						<h3 className={classNames("modal-card-title", props.secondaryTitle && "with-subtitle")}>{props.title}</h3>
					)}
					{props.secondaryTitle && <h5 className="modal-card-secondary-title">{props.secondaryTitle}</h5>}
					{content}
				</div>
			</form>
		</div>
	)
}
